import { Controller } from '@hotwired/stimulus';

/**
 * Contrôleur Stimulus pour les déclencheurs de modale de confirmation
 * Ce contrôleur est attaché aux boutons/liens qui doivent ouvrir la modale
 *
 * ⚠️ `message` est du TEXTE, jamais du HTML — le fragment en emphase passe par
 * `messageHighlight`, le contenu riche par `messageTemplate`. Voir l'entête de
 * `confirmation_modal_controller.js` pour le pourquoi (XSS QA #556).
 *
 * Utilisation :
 * <a href="/path/to/delete"
 *    data-controller="confirmation-modal-trigger"
 *    data-confirmation-modal-trigger-title-value="Supprimer l'entreprise"
 *    data-confirmation-modal-trigger-message-value="Êtes-vous sûr de vouloir supprimer %{highlight} ?"
 *    data-confirmation-modal-trigger-message-highlight-value="<%= contractor.contractor_name %>"
 *    data-confirmation-modal-trigger-confirm-text-value="Supprimer"
 *    data-confirmation-modal-trigger-method-value="delete"
 *    data-action="click->confirmation-modal-trigger#open">
 */
export default class extends Controller {
  static values = {
    title: { type: String, default: "Confirmer l'action" },
    // TEXTE, jamais du HTML — cf. l'entête de confirmation_modal_controller.js.
    // Peut porter le slot `%{highlight}` et des `\n` (rendus en <br>).
    message: { type: String, default: 'Êtes-vous sûr de vouloir continuer ?' },
    // Le fragment mis en emphase, en pratique la donnée NON FIABLE. Passé tel
    // quel (sans h(), sans .html_safe) : le JS l'insère en nœud texte.
    messageHighlight: String,
    // Id d'un <template> rendu par le serveur, pour un message riche (liens,
    // icônes, champs). Prioritaire sur `message`.
    messageTemplate: String,
    confirmText: { type: String, default: 'Confirmer' },
    method: { type: String, default: 'delete' },
    iconColor: { type: String, default: 'red' }, // Couleur de l'icône (red, green, blue, yellow)
    url: String, // Optionnel, sinon on prend le href du lien
  };

  connect() {
    this.element.setAttribute('data-turbo-prefetch', 'false');
  }

  /**
   * Ouvre la modale de confirmation globale
   */
  open(event) {
    event.preventDefault();

    // Trouver la modale globale dans le DOM
    const modal = document.querySelector('[data-controller="confirmation-modal"]');

    if (!modal) {
      console.error('Confirmation modal not found in the DOM');
      return;
    }

    // Récupérer le contrôleur de la modale
    const modalController = this.application.getControllerForElementAndIdentifier(
      modal,
      'confirmation-modal'
    );

    if (!modalController) {
      console.error('Confirmation modal controller not found');
      return;
    }

    // Préparer l'événement avec les données
    const triggerElement = event.currentTarget;
    triggerElement.dataset.confirmationModalTriggerTitle = this.titleValue;
    triggerElement.dataset.confirmationModalTriggerMessage = this.messageValue;
    triggerElement.dataset.confirmationModalTriggerMessageHighlight = this.messageHighlightValue;
    triggerElement.dataset.confirmationModalTriggerMessageTemplate = this.messageTemplateValue;
    triggerElement.dataset.confirmationModalTriggerConfirmText = this.confirmTextValue;
    triggerElement.dataset.confirmationModalTriggerMethod = this.methodValue;
    triggerElement.dataset.confirmationModalTriggerIconColor = this.iconColorValue;

    // Si URL n'est pas fourni explicitement, utiliser le href
    if (!this.hasUrlValue && triggerElement.href) {
      triggerElement.dataset.confirmationModalTriggerUrl = triggerElement.href;
    } else if (this.hasUrlValue) {
      triggerElement.dataset.confirmationModalTriggerUrl = this.urlValue;
    }

    // Ouvrir la modale
    modalController.open(event);
  }
}
